import type { HelpContent } from './types';

export const wipReportingHelp: HelpContent = {
  pageTitle: 'WIP Reporting',
  description: 'Review Work in Progress across all projects — track unbilled time, upcoming billing events, and ready-to-bill items in one place.',

  functionality: {
    overview: 'The WIP Reporting page gives you a real-time view of work that has been completed but not yet billed. It helps you identify which projects are ready to bill, what billing events are coming up, and where revenue is at risk of being left unbilled.',

    keyFeatures: [
      {
        label: 'WIP summary cards',
        detail: 'KPI cards at the top of the page show total WIP value, ready-to-bill value, billing due this month, and overdue billing at a glance.',
      },
      {
        label: 'Ready-to-bill indicators',
        detail: 'Projects that are ready to bill are clearly flagged, making it easy to prioritise which projects need invoicing action.',
      },
      {
        label: 'Billing event tracking',
        detail: 'See upcoming and overdue billing events for each project, including trigger type (date-based or percentage complete) and the next billing event date.',
      },
      {
        label: 'Project type filtering',
        detail: 'Filter the WIP list by project type — Time & Materials or Fixed Fee — to focus on the billing model relevant to you.',
      },
      {
        label: 'Search and date range filters',
        detail: 'Search by project name or client, and filter by a custom date range to narrow down the WIP view to a specific period.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the full WIP report to Excel, respecting any active filters so you get exactly the data visible on screen.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Displays four key metrics at the top of the page: Total WIP, Ready to Bill, Due This Month, and Overdue. Each card shows the value and a count of affected projects.',
      },
      {
        name: 'Search bar',
        description: 'Filter the WIP list in real time by project name or client name.',
      },
      {
        name: 'Project type filter',
        description: 'Filter by project type — Time & Materials (T&M) or Fixed Fee. Select "All" to see every project type.',
      },
      {
        name: 'Date range filter',
        description: 'Restrict the WIP view to a specific date range using the date picker. Useful for period-end reporting.',
      },
      {
        name: 'WIP table',
        description: 'Lists all projects with outstanding WIP. Columns include project name, client, project type, project manager, WIP value, next billing event, trigger type, and ready-to-bill status.',
      },
      {
        name: 'Ready to bill badge',
        description: 'A green badge indicating the project has met its billing trigger conditions and is ready to be invoiced.',
      },
      {
        name: 'Next billing event',
        description: 'Shows the date or percentage threshold that will trigger the next billing event for the project.',
      },
      {
        name: 'Export button',
        description: 'Downloads the current WIP report as an Excel file, reflecting any active filters and search terms.',
      },
    ],

    useCases: [
      'Identifying all projects that are ready to bill ahead of a billing run.',
      'Reviewing upcoming billing events to plan invoicing activity.',
      'Tracking overdue billing to prevent revenue slippage.',
      'Filtering WIP by project type to focus on a specific billing model.',
      'Exporting WIP data for use in finance or billing workflows.',
    ],
  },

  userGuide: {
    gettingStarted: 'The WIP Reporting page loads with all active projects that have outstanding work in progress. Use the KPI cards to get an immediate overview, then use the filters to focus on specific projects or billing scenarios.',
    steps: [
      {
        title: 'How do I find projects that are ready to bill?',
        items: [
          { text: 'Look for the green "Ready to Bill" badge in the WIP table.' },
          { text: 'The "Ready to Bill" KPI card at the top shows the total value and count of projects ready for invoicing.' },
          { text: 'Use the project type filter to focus on a specific billing model if needed.' },
        ],
      },
      {
        title: 'How do I filter the WIP report?',
        items: [
          { text: 'Use the search bar to filter by project name or client.' },
          { text: 'Use the project type filter to narrow by T&M or Fixed Fee.' },
          { text: 'Use the date range filter to restrict results to a specific period.' },
          { text: 'All filters can be combined and cleared individually.' },
        ],
      },
      {
        title: 'How do I export the WIP report?',
        items: [
          { text: 'Apply any filters you need to refine the data.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The report downloads as an Excel file reflecting the current filtered view.' },
        ],
      },
    ],
    proTips: [
      'Use the "Due This Month" KPI card as your starting point at the beginning of each billing period — it shows exactly what needs to be invoiced this month.',
      'The date range filter is especially useful for period-end reporting — set it to your billing period to see only the WIP relevant to that period.',
      'Export the full unfiltered report at month end to keep a record of WIP for your finance team.',
    ],
  },

  faq: [
    {
      question: 'What does WIP mean?',
      answer: 'WIP stands for Work in Progress — it refers to work that has been completed and costs incurred, but has not yet been billed to the client.',
    },
    {
      question: 'What does "Ready to Bill" mean?',
      answer: 'A project is marked as Ready to Bill when it has met its billing trigger conditions — either a billing date has been reached or a percentage completion threshold has been met.',
    },
    {
      question: 'What is the difference between "Due This Month" and "Overdue"?',
      answer: '"Due This Month" shows billing events that fall within the current calendar month. "Overdue" shows billing events whose trigger date has already passed without being billed.',
    },
    {
      question: 'Can I create an invoice from this page?',
      answer: 'No. The WIP Reporting page is a read-only reporting view. Use it to identify what needs to be billed, then raise invoices through your billing workflow.',
    },
    {
      question: 'Why is a project not appearing in the WIP report?',
      answer: 'Projects only appear in the WIP report if they have outstanding unbilled work. If a project has no WIP, it will not be listed. Check the project\'s billing tab to confirm its billing status.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Profitability Report', path: '/reports/profitability' },
  ],
};
